import React, { useEffect, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  ExclamationCircleIcon,
  ListBulletIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import api from '../../../../api';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../../../Layouts/context/Authcontext';

ChartJS.register(ArcElement, Tooltip, Legend);

const QAPieChart = ({ filters }) => {
  const { accessToken } = useAuth();
  const [top5Users, setTop5Users] = useState([]);
  const [errorData, setErrorData] = useState([]);
  const [topErrors, setTopErrors] = useState([]);

  const [categoryRawLabels, setCategoryRawLabels] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState([]);
  const [categoryData, setCategoryData] = useState([]);

  const [statusRawLabels, setStatusRawLabels] = useState([]);
  const [statusCounts, setStatusCounts] = useState([]);
  const [statusData, setStatusData] = useState([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const filteredParams = {};
        Object.keys(filters || {}).forEach((key) => {
          if (filters[key]) filteredParams[key] = filters[key];
        });

        const baseUrl = getRoundRobinBaseUrl();

        // 1. Top 5 Error Users
        const response1 = await api.get(`${baseUrl}/superAdminDashboard/getTop5ErrorUserQualityPercentage`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: filteredParams,
        });

        if (response1.data.success && response1.data.data) {
          const top5Errors = response1.data.data.top5ErrorUsers || [];
          setTop5Users(top5Errors);
          setTopErrors(top5Errors.map((item) => item.employeeName));
          setErrorData(top5Errors.map((item) => item.qualityPercentage || 1));
        }

        // 2. Error Category Wise Percentage
        const response2 = await api.get(`${baseUrl}/superAdminDashboard/getErrorCategoryWisePercentage`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: filteredParams,
        });

        if (response2.data.success && response2.data.data) {
          const categories = response2.data.data.errorTypePercentage || [];
          setCategoryRawLabels(categories.map((c) => c._id));
          setCategoryCounts(categories.map((c) => c.errorTypeCount));
          setCategoryData(categories.map((c) => c.errorTypePercentage));
        }

        // 3. Audit Status Closed and Open Percentage
        const response3 = await api.get(`${baseUrl}/superAdminDashboard/getAuditStatusClosedAndOpenPercentage`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: filteredParams,
        });

        if (response3.data.success && response3.data.data) {
          const statuses = response3.data.data.auditStatusPercentage || [];
          setStatusRawLabels(statuses.map((s) => s._id));
          setStatusCounts(statuses.map((s) => s.auditStatusCount));
          setStatusData(statuses.map((s) => s.auditStatusPercentage));
        }
      } catch (error) {
        console.error('Error fetching QA Pie Chart data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (filters) fetchData();
  }, [filters, accessToken]);

  const hasNoData = !loading && (topErrors.length === 0 || errorData.every((val) => val === 0 || val == null));
  const hasNoCategoryData = !loading && (categoryRawLabels.length === 0 || categoryData.every((val) => val === 0 || val == null));
  const hasNoStatusData = !loading && (statusRawLabels.length === 0 || statusData.every((val) => val === 0 || val == null));

  // Build category colors dynamically
  const defaultColors = ['#EE1D23', '#6B7280', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6'];
  const categoryColors = categoryRawLabels.map(label => {
    if (label === 'Non Fatal') {
      return '#1A2B5C';
    }
    return defaultColors.shift() || '#6B7280';
  });

  const categoryPieData = {
    labels: categoryRawLabels,
    datasets: [{
      label: 'Error Categories',
      data: categoryData,
      backgroundColor: categoryColors,
      borderWidth: 1,
    }],
  };

  const errorPieData = {
    labels: topErrors,
    datasets: [{
      label: 'Top 5 Errors',
      data: errorData,
      backgroundColor: ['#EE1D23', '#6B7280', '#1A2B5C', '#3B82F6', '#10B981'],
      borderWidth: 1,
    }],
  };

  const statusPieData = {
    labels: statusRawLabels,
    datasets: [{
      label: 'Audit Status',
      data: statusData,
      backgroundColor: ['#1A2B5C', '#EE1D23'],
      borderWidth: 1,
    }],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          font: { size: 13, weight: '500' },
          color: '#1F2937',
        },
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const index = tooltipItem.dataIndex;
            const dataset = tooltipItem.dataset;
            const value = dataset.data[index] || 0;
            const label = tooltipItem.label || '';

            if (dataset.label === 'Audit Status') {
              const count = statusCounts[index];
              return [`${label}`, `Count: ${count}`, `Percentage: ${value.toFixed(2)}%`];
            } else if (dataset.label === 'Error Categories') {
              const count = categoryCounts[index];
              return [`${label}`, `Count: ${count}`, `Percentage: ${value.toFixed(2)}%`];
            } else {
              return `${label}: ${value.toFixed(2)}%`;
            }
          },
        },
      },
    },
  };

  const NoDataMessage = () => (
    <div className="flex flex-col items-center justify-center h-full text-center animate-fadeIn">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-[#EE1D23]/70 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.75V3m0 18v-1.75M6.364 6.364l-1.061-1.061m12.728 12.728l-1.061-1.061M4.75 12H3m18 0h-1.75M6.364 17.636l-1.061 1.061m12.728-12.728l-1.061 1.061" />
      </svg>
      <div className="text-lg font-semibold text-[#1A2B5C] mb-1">No Data Available</div>
      <p className="text-sm text-gray-500 max-w-sm">No information found for the selected scope. Try refreshing or adjusting your filters.</p>
    </div>
  );

  return (
    <div className="flex flex-wrap gap-12 justify-center mt-14 px-6">
      {/* QA Error Breakdown */}
      <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-sm">
        <div className="flex items-center justify-center mb-4">
          <ExclamationCircleIcon className="w-6 h-6" style={{ color: '#EE1D23' }} />
          <h2 className="text-lg font-semibold text-gray-700 ml-2">QA Error Breakdown</h2>
        </div>
        <div className="w-full h-64">
          {loading ? <div className="flex justify-center items-center h-full">Loading...</div> : hasNoCategoryData ? <NoDataMessage /> : <Pie data={categoryPieData} options={pieOptions} />}
        </div>
      </div>

      {/* Top 5 Error Users */}
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm border border-gray-200 backdrop-blur-sm">
        <div className="bg-gradient-to-r from-[#1A2B5C] to-[#3B4A6B] px-5 py-4 flex items-center">
          <ListBulletIcon className="w-6 h-6 text-white mr-2" />
          <h2 className="text-white font-semibold text-lg tracking-wide">Top 5 Error Users</h2>
        </div>
        <div className="h-64 overflow-y-auto divide-y divide-gray-100">
          {loading ? (
            <div className="px-5 py-4 text-center text-sm text-gray-500">Loading...</div>
          ) : hasNoData ? (
            <NoDataMessage />
          ) : (
            <ul>
              {top5Users.map((user, index) => (
                <li key={user._id} className="flex items-center justify-between px-5 py-3 group hover:bg-gradient-to-r hover:from-[#F1F5F9] hover:to-[#E0E7FF] transition-all duration-300">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#EE1D23] to-[#1A2B5C] text-white flex items-center justify-center font-bold text-sm shadow-md group-hover:scale-110 group-hover:rotate-6 transition-transform">
                      {index + 1}
                    </div>
                    <div className="text-sm text-gray-800 font-medium truncate max-w-[120px] group-hover:text-[#1A2B5C]">
                      {user.employeeName}
                    </div>
                  </div>
                  <div className="flex flex-col items-end text-right">
                    <div className="text-xs text-gray-500 font-mono tracking-tight">ID: {user._id}</div>
                    <div className="text-sm font-semibold text-[#EE1D23]">{user.qualityPercentage.toFixed(2)}%</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Status Overview */}
      <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-sm">
        <div className="flex items-center justify-center mb-4">
          <CheckCircleIcon className="w-6 h-6" style={{ color: '#1A2B5C' }} />
          <h2 className="text-lg font-semibold text-gray-700 ml-2">Status Overview</h2>
        </div>
        <div className="w-full h-64">
          {loading ? <div className="flex justify-center items-center h-full">Loading...</div> : hasNoStatusData ? <NoDataMessage /> : <Pie data={statusPieData} options={pieOptions} />}
        </div>
      </div>
    </div>
  );
};

export default QAPieChart;
